import React, { useState, useEffect } from 'react';
import { Clock, Trophy, CheckCircle, XCircle, ArrowLeft, ArrowRight, Share2, Star, Zap, Award, Menu, Users, Brain, Sparkles, Crown, Rocket, TrendingUp, Shield, CheckSquare, Play, Lightbulb, Target, Shuffle, GraduationCap, Briefcase } from 'lucide-react';
import { Question, SelectedAnswers } from './data';
import { useLocalization } from './hooks/useLocalization';
import LanguageSelector from './components/LanguageSelector';
import { isFeatureEnabled } from './utils/accessCodes';
import { isMobileDevice } from './utils/mobileUtils';

// Import report generation system
// Note: Using dynamic imports to avoid build issues for now
// import MainProcessor from '../../main_processor.js';
// import InteractiveReport from '../../interactive_report.js';

interface ISTQBAIFullQuizProps {
  onBuyFullVersion?: () => void;
}

const ISTQBAIFullQuiz = ({ onBuyFullVersion: _onBuyFullVersion }: ISTQBAIFullQuizProps) => {
  // Localization hook
  const { currentLocale, changeLocale, uiTexts, fullQuestionPool } = useLocalization();
  
  // Отключаем анимации на мобильных устройствах при инициализации
  useEffect(() => {
    if (isMobileDevice()) {
      document.body.classList.add('mobile-no-animations');
    }
  }, []);
  
  const [currentView, setCurrentView] = useState<string>('welcome');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswers>({});
  // Убрали lockedAnswers - теперь можно менять ответы
  const [timeLeft, setTimeLeft] = useState<number>(2400); // 40 minutes for full exam
  const [quizStarted, setQuizStarted] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [_score, setScore] = useState<number>(0); // Сохраняем для backward compatibility, префикс _ показывает что не используется в UI
  const [showDetailedResults, setShowDetailedResults] = useState<boolean>(false);
  const [_currentStreak, setCurrentStreak] = useState<number>(0); // Не используется в UI, только для расчетов
  const [maxStreak, setMaxStreak] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [__debugMode, _setDebugMode] = useState<boolean>(false);
  const [testPerfectScore, setTestPerfectScore] = useState<boolean>(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Навигация клавишами влево-вправо (только для десктопа)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Проверяем что мы в режиме квиза и это десктоп
      if (!quizStarted || showResults || window.innerWidth < 768) return;
      
      if (event.key === 'ArrowLeft' && currentQuestion > 0) {
        setCurrentQuestion(currentQuestion - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowRight' && currentQuestion < selectedQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [quizStarted, showResults, currentQuestion, selectedQuestions.length]);

  // Full question pool - 300+ questions (ISTQB AI Testing Syllabus)
  // Now imported from external file: src/data/locales/en/questions.ts


  // Function to randomly select 40 questions from the full pool
  const selectRandomQuestions = (): Question[] => {
    const pool = [...fullQuestionPool];
    const shuffled = pool.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 40);
  };

  // Scroll to top on component mount and view changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentView, showResults]);

  // Update selected questions when locale changes during quiz or in results
  useEffect(() => {
    if (selectedQuestions.length > 0) {
      // Map current questions to their translated versions
      // Preserve the same question IDs to maintain progress and answers
      const updatedQuestions = selectedQuestions.map(currentQ => {
        // Find the same question (by ID) in the new language pool
        const translatedQuestion = fullQuestionPool.find(q => q.id === currentQ.id);
        
        // If found, use the translated version; otherwise, keep the current one
        return translatedQuestion || currentQ;
      });
      
      // Update the questions with the translated versions
      setSelectedQuestions(updatedQuestions);
      
      // All other state (currentQuestion, selectedAnswers, showResults, score) remains unchanged
      // This ensures seamless language switching without losing progress
    }
  }, [currentLocale, fullQuestionPool]);

  // Debug mode activation - DISABLED
  useEffect(() => {
    // Debug modes are disabled in production
    // All debug functionality has been turned off per user request
    
    /*
    // Check URL parameters for debug mode
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    const testParam = urlParams.get('test');
    
    if (debugParam === 'true' || debugParam === '1') {
      setDebugMode(true);
      console.log('🔧 Debug mode activated via URL parameter');
    }
    
    if (testParam === 'perfect' || testParam === '100') {
      setTestPerfectScore(true);
      console.log('🎯 Test perfect score activated via URL parameter');
    }

    // Keyboard shortcut for debug mode (Ctrl+Shift+D)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setDebugMode(prev => {
          console.log(prev ? '🔧 Debug mode deactivated' : '🔧 Debug mode activated');
          return !prev;
        });
      }
    */
      
      // All debug functionality disabled
    // Debug functionality completely removed per user request
  }, []);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (quizStarted && timeLeft > 0 && !showResults) {
      timer = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            handleFinishQuiz();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [quizStarted, timeLeft, showResults]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to get explanation for specific answer choice
  const getExplanationForAnswer = (question: Question, answerIndex: number): string => {
    switch (answerIndex) {
      case 0: return question.expA;
      case 1: return question.expB;
      case 2: return question.expC;
      case 3: return question.expD;
      default: return question.expA; // fallback to first explanation
    }
  };

  // Generate full interactive report with complete analysis
  const generateFullReport = async () => {
    setIsGeneratingReport(true);
    setReportError(null);
    
    try {
      // Собираем данные в формате, ожидаемом QuizAnalyzer
      // Фильтруем только отвеченные вопросы для анализа
      const answeredQuestions = selectedQuestions.filter(question => selectedAnswers[question.id] !== undefined);
      const skippedQuestions = selectedQuestions.filter(question => selectedAnswers[question.id] === undefined);
      
      console.log(`📊 Статистика: отвечено ${answeredQuestions.length}, пропущено ${skippedQuestions.length}`);

      const quizResults = {
        answers: answeredQuestions.map((question) => {
          const userAnswer = selectedAnswers[question.id];
          const isCorrect = userAnswer === question.correct;
          
          return {
            questionId: question.id,
            chapter: question.chapter,
            difficulty: question.difficulty,
            points: getDifficultyPoints(question.difficulty),
            userAnswer: ['A', 'B', 'C', 'D'][userAnswer], // Теперь гарантированно не undefined
            correctAnswer: ['A', 'B', 'C', 'D'][question.correct],
            isCorrect,
            timeSpent: estimateTimeSpent(question.difficulty, isCorrect) // Умная оценка времени
          };
        }),
        totalQuestions: selectedQuestions.length, // Общее количество вопросов
        answeredQuestions: answeredQuestions.length, // Количество отвеченных
        totalTime: timeLeft > 0 ? (2400 - timeLeft) : 2400,
        questionsRevisited: 0, // TODO: добавить отслеживание
        questionsSkipped: skippedQuestions.length,
        skippedQuestionIds: skippedQuestions.map(q => q.id), // ID пропущенных вопросов
        sessionId: `session_${Date.now()}`,
        timestamp: new Date().toISOString(),
        language: currentLocale,
        version: 'full',
        deviceType: window.innerWidth < 768 ? 'mobile' : 'desktop'
      };

      console.log('🔍 Проверка данных перед отправкой в MainProcessor:');
      console.log('📊 Количество данных:', {
        totalQuestions: quizResults.totalQuestions,
        answeredQuestions: quizResults.answeredQuestions,
        skippedQuestions: quizResults.questionsSkipped,
        answersDataSize: quizResults.answers.length,
        memoryUsage: (performance as any)?.memory ? {
          used: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024) + 'MB',
          total: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024) + 'MB'
        } : 'unavailable'
      });

      console.log('🧠 Generating HTML report...');

      // Генерируем HTML отчет
      const reportId = quizResults.sessionId;
      
      try {
        // Динамически загружаем генератор отчетов
        // @ts-ignore
        const { default: QuizAnalyzer } = await import('../quiz_analyzer.js');
        // @ts-ignore
        const { default: InsightGenerator } = await import('../insight_generator.js');
        // @ts-ignore
        const { default: ReportGenerator } = await import('../report_generator.js');
        // @ts-ignore
        const { default: InteractiveReport } = await import('../interactive_report.jsx');
        
        console.log('📊 Processing quiz results...');
        
        // Анализируем результаты квиза
        const analyzer = new QuizAnalyzer();
        const analysisResults = analyzer.analyzeResults(quizResults);
        
        // Генерируем инсайты
        const insightGenerator = new InsightGenerator();
        const insights = insightGenerator.generateInsights(analysisResults);
        
        // Создаем структурированный отчет
        const reportGenerator = new ReportGenerator();
        const structuredReport = reportGenerator.generateReport(insights, analysisResults);
        
        // Генерируем HTML
        const reportHTML = InteractiveReport(structuredReport);
        
        console.log('✅ HTML report generated successfully');
        
        // Сохраняем и данные квиза, и HTML отчет
        localStorage.setItem(`quiz_results_${reportId}`, JSON.stringify(quizResults));
        localStorage.setItem(`report_${reportId}`, JSON.stringify(reportHTML));
        sessionStorage.setItem('current_quiz_results', JSON.stringify(quizResults));
        sessionStorage.setItem(`current_report_html`, JSON.stringify(reportHTML));
        
      } catch (error) {
        console.error('❌ Error generating HTML report:', error);
        
        // Fallback: сохраняем только данные квиза
        localStorage.setItem(`quiz_results_${reportId}`, JSON.stringify(quizResults));
        sessionStorage.setItem('current_quiz_results', JSON.stringify(quizResults));
        
        // Создаем простой HTML fallback
        const fallbackHTML = `
          <div style="padding: 40px; text-align: center; color: white;">
            <h2>📊 Анализ результатов квиза</h2>
            <div style="background: rgba(17, 24, 39, 0.8); padding: 30px; border-radius: 20px; margin: 20px 0;">
              <h3>📈 Ваши результаты:</h3>
              <p><strong>Правильных ответов:</strong> ${quizResults.answers.filter(a => a.isCorrect).length} из ${quizResults.totalQuestions}</p>
              <p><strong>Процент успеха:</strong> ${Math.round((quizResults.answers.filter(a => a.isCorrect).length / quizResults.totalQuestions) * 100)}%</p>
              <p><strong>Время прохождения:</strong> ${Math.round(quizResults.totalTime / 60)} минут</p>
            </div>
            <p style="opacity: 0.7;">Полный анализ с рекомендациями будет доступен в следующем обновлении.</p>
          </div>
        `;
        
        localStorage.setItem(`report_${reportId}`, JSON.stringify(fallbackHTML));
      }
      
      console.log('✅ Report data saved. Opening report inline...');
      
      // Показываем отчет в inline модальном окне вместо popup
      const reportLink = `/report.html?id=${reportId}`;
      console.log('🔗 Setting report URL:', reportLink);
      console.log('📊 Full Report URL:', window.location.origin + reportLink);
      
      setReportUrl(reportLink);
      console.log('🎯 showReportModal before:', showReportModal);
      
      // Принудительно очистим состояние перед установкой нового
      setShowReportModal(false);
      setReportError(null);
      
      // Используем requestAnimationFrame чтобы обновления применились
      requestAnimationFrame(() => {
        console.log('🎯 Setting showReportModal to true in next frame...');
        setShowReportModal(true);
        console.log('✅ showReportModal set to true in requestAnimationFrame');
      });
      
      // Проверяем что состояние действительно изменилось
      setTimeout(() => {
        console.log('🔍 Modal state check after timeout - using refs to get current values');
        // Проверяем DOM напрямую чтобы убедиться в рендеринге
        const modalElement = document.querySelector('[class*="fixed"][class*="inset-0"]');
        console.log('🔍 Modal DOM element found:', !!modalElement);
      }, 100);
      
      console.log('🚀 Report modal setup completed successfully');

    } catch (error: any) {
      console.error('❌ Error generating report:', error);
      console.error('🔍 Error details:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        cause: error?.cause,
        dataSize: selectedQuestions.length,
        isOutOfMemory: error?.message?.includes('memory') || error?.name === 'RangeError',
        errorType: typeof error
      });
      
      const errorMessage = error?.message || 'Произошла ошибка при генерации отчета';
      const isMemoryError = error?.message?.includes('memory') || error?.message?.includes('Maximum call stack');
      
      if (isMemoryError) {
        setReportError('Слишком много данных для обработки. Попробуйте ответить на меньшее количество вопросов.');
        alert('⚠️ Слишком много данных для анализа. Система оптимизируется для больших объемов данных...');
      } else {
        setReportError(errorMessage);
        alert(`❌ Ошибка: ${errorMessage}`);
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Helper functions for data preparation
  const getDifficultyPoints = (difficulty: string): number => {
    const pointsMap = { K1: 1, K2: 2, K3: 2, K4: 3 };
    return pointsMap[difficulty as keyof typeof pointsMap] || 1;
  };

  const estimateTimeSpent = (difficulty: string, isCorrect: boolean): number => {
    const baseTime = { K1: 35, K2: 50, K3: 65, K4: 80 };
    const base = baseTime[difficulty as keyof typeof baseTime] || 45;
    
    // Добавляем вариативность
    const variance = (Math.random() - 0.5) * 20; // ±10 секунд
    const correctnessModifier = isCorrect ? 0 : Math.random() * 15; // Неправильные ответы могут занимать больше времени
    
    return Math.max(15, Math.round(base + variance + correctnessModifier));
  };

  // Mobile haptic feedback simulation
  const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (navigator.vibrate) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 50
      };
      navigator.vibrate(patterns[type]);
    }
  };

  const handleAnswerSelect = (questionId: number, answerIndex: number): void => {
    // Убираем блокировку ответов - теперь можно менять выбор
    
    // Haptic feedback - убираем зависимость от правильности ответа
    triggerHapticFeedback('light');
    
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));

    // Убираем блокировку и обновление streak во время прохождения
    // Streak будет рассчитываться только в результатах
  };

  // Touch handlers for swipe navigation with improved angle detection
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Check if swipe distance is sufficient
    if (distance < minSwipeDistance) return;
    
    // Calculate angle of swipe (in degrees)
    const angle = Math.abs(Math.atan2(deltaY, deltaX) * 180 / Math.PI);
    
    // Only register horizontal swipes (angle between 0-30° or 150-180°)
    const isHorizontalSwipe = angle <= 30 || angle >= 150;
    
    // Additionally check that horizontal movement is at least 2x larger than vertical
    const isHorizontalDominant = Math.abs(deltaX) >= Math.abs(deltaY) * 2;
    
    if (isHorizontalSwipe && isHorizontalDominant) {
      const isLeftSwipe = deltaX > 0;
      const isRightSwipe = deltaX < 0;

      if (isLeftSwipe && currentQuestion < selectedQuestions.length - 1) {
        handleNextQuestion();
      }
      if (isRightSwipe && currentQuestion > 0) {
        handlePrevQuestion();
      }
    }
  };

  const handleNextQuestion = (): void => {
    if (currentQuestion < selectedQuestions.length - 1) {
      triggerHapticFeedback('light');
      setCurrentQuestion(prev => prev + 1);
      // Smooth scroll to top on mobile
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevQuestion = (): void => {
    if (currentQuestion > 0) {
      triggerHapticFeedback('light');
      setCurrentQuestion(prev => prev - 1);
      // Smooth scroll to top on mobile
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleFinishQuiz = (): void => {
    triggerHapticFeedback('heavy');
    let totalScore = 0;
    selectedQuestions.forEach(question => {
      if (selectedAnswers[question.id] === question.correct) {
        totalScore += question.points;
      }
    });
    setScore(totalScore);
    
    // Рассчитываем maxStreak на основе финальных ответов
    const finalMaxStreak = calculateMaxStreak();
    setMaxStreak(finalMaxStreak);
    
    setShowResults(true);
    setQuizStarted(false);
    // Smooth scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const setCurrentQuestionWithFeedback = (questionIndex: number): void => {
    triggerHapticFeedback('light');
    setCurrentQuestion(questionIndex);
    // Smooth scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const startQuiz = (): void => {
    triggerHapticFeedback('medium');
    const randomQuestions = selectRandomQuestions();
    setSelectedQuestions(randomQuestions);
    setCurrentView('quiz');
    setQuizStarted(true);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(2400); // 40 minutes
    setShowResults(false);
    setShowDetailedResults(false);
    setCurrentStreak(0);
    setMaxStreak(0);
    setTouchStart(null);
    setTouchEnd(null);
    
    // Debug mode: Auto-fill all correct answers - DISABLED
    if (false && testPerfectScore) {
      const perfectAnswers: SelectedAnswers = {};
      randomQuestions.forEach(question => {
        perfectAnswers[question.id] = question.correct;
      });
      setSelectedAnswers(perfectAnswers);
      setMaxStreak(randomQuestions.length);
      console.log('🎯 Debug: Auto-filled all correct answers for perfect score test');
    }
    
    // Scroll to top when starting quiz
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Prevent zoom on mobile
    if (window.innerWidth < 768) {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
      }
    }
  };

  const goToStart = (): void => {
    // Return to start page so user can change language if needed
    setCurrentView('welcome');
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(2400);
    setShowResults(false);
    setScore(0);
    setShowDetailedResults(false);
    setCurrentStreak(0);
    setMaxStreak(0);
    setTouchStart(null);
    setTouchEnd(null);
    // Don't clear selectedQuestions - keep them available for restart
    // User can start a new quiz with the same or new questions from intro screen
    
    // Scroll to top when going to start
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // 🔧 Подсчет количества правильных ответов
  const getCorrectAnswersCount = (): number => {
    return selectedQuestions.filter(question => 
      selectedAnswers[question.id] === question.correct
    ).length;
  };

  // 🔧 Правильный подсчет процента на основе количества правильных ответов
  const getScorePercentage = (): number => {
    const correctCount = getCorrectAnswersCount();
    return Math.round((correctCount / selectedQuestions.length) * 100);
  };

  // 🔧 Правильная проверка прохождения на основе процента правильных ответов
  const isPassed = (): boolean => {
    const correctCount = getCorrectAnswersCount();
    return correctCount >= Math.ceil(selectedQuestions.length * 0.65); // 65% правильных ответов как в реальном ISTQB
  };

  // 🔧 Проверка идеального результата на основе количества правильных ответов
  const isPerfectScore = (): boolean => {
    // Test mode override for debugging
    if (testPerfectScore) {
      return true;
    }
    const correctCount = getCorrectAnswersCount();
    return correctCount === selectedQuestions.length; // Все 40 вопросов правильно
  };

  // 🔧 Расчет максимального streak на основе финальных ответов
  const calculateMaxStreak = (): number => {
    let maxStreak = 0;
    let currentStreak = 0;
    
    selectedQuestions.forEach(question => {
      const isCorrect = selectedAnswers[question.id] === question.correct;
      if (isCorrect) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    });
    
    return maxStreak;
  };

  const getPerformanceLevel = (): { level: string; emoji: string; message: string } => {
    const percentage = getScorePercentage();
    if (isPerfectScore()) return { level: uiTexts.results.performance.perfectMaster, emoji: "👑✨🏆", message: uiTexts.results.sharing.perfectScore.hiddenModeUnlocked };
    if (percentage >= 90) return { level: uiTexts.results.performance.master, emoji: "🏆✨", message: uiTexts.results.congratulations };
    if (percentage >= 80) return { level: uiTexts.results.performance.expert, emoji: "🎯🔥", message: uiTexts.results.congratulations };
    if (percentage >= 70) return { level: uiTexts.results.performance.professional, emoji: "🚀💫", message: uiTexts.results.congratulations };
    if (percentage >= 65) return { level: uiTexts.results.performance.candidate, emoji: "📚⚡", message: uiTexts.results.congratulations };
    return { level: uiTexts.results.performance.learner, emoji: "🌱💪", message: uiTexts.results.congratulations };
  };

  const generateShareableResult = (): { text: string; hashtags: string[] } => {
    const percentage = getScorePercentage();
    const correctCount = getCorrectAnswersCount();
    const passed = isPassed();
    const perfect = isPerfectScore();
    
    if (perfect) {
      // Special Easter Egg text for perfect score
      return {
        text: `🎊🏆 INCREDIBLE! I just achieved a PERFECT 100% on the ISTQB AI Tester Full Quiz! 🏆🎊\n\n👑 HIDDEN MODE UNLOCKED! 👑\n📊 Score: 100% (40/40 questions PERFECT!)\n🎯 Status: ABSOLUTE MASTERY\n🔥 Achievement: QUIZ CHAMPION\n\n✨ This wasn't just a test - it was a journey to perfection! ✨\n\n🎁 I've unlocked the secret achievement and I'm ready to share this incredible milestone with the AI Testing community!\n\n💎 Ready to challenge yourself? Try the demo first!\n\n#ISTQB #AITesting #Ainastasia #AIQuiz ${uiTexts.results.sharing.perfectScore.hashtag}`,
        hashtags: ['ISTQB', 'AITesting', 'Certification', 'TechSkills', 'QualityAssurance', 'PerfectScore', uiTexts.results.sharing.perfectScore.hashtag.replace('#', '')]
      };
    }
    
    const emoji = passed ? '🎉' : '💪';
    const status = passed ? 'PASSED' : 'READY TO IMPROVE';
    
    return {
      text: `${emoji} Just completed the ISTQB AI Tester Full Quiz!\n\n📊 Score: ${percentage}% (${correctCount}/${selectedQuestions.length} correct)\n🎯 Status: ${status}\n\n${passed ? '✅ Certification ready!' : '🔥 Continuing my preparation!'}\n\n#ISTQB #AITesting #Ainastasia #AIQuiz`,
      hashtags: ['ISTQB', 'AITesting', 'Certification', 'TechSkills', 'QualityAssurance']
    };
  };

  const shareToLinkedIn = async (): Promise<void> => {
    const result = generateShareableResult();
    const shareText = result.text;
    const demoUrl = window.location.origin; // Link to demo version from full version
    const fullShareText = `${shareText}\n\n🎯 Try the free demo yourself: ${demoUrl}`;
    
    // Function to detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create LinkedIn share URL with pre-filled text
    const createLinkedInShareUrl = (text: string): string => {
      const encodedText = encodeURIComponent(text);
      // Use the new LinkedIn sharing format that works well on both web and mobile
      return `https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}`;
    };
    
    const linkedInShareUrl = createLinkedInShareUrl(fullShareText);
    
    // Try Web Share API first (modern browsers, especially mobile)
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: 'ISTQB AI Tester Certification Quiz Result',
          text: shareText,
          url: demoUrl
        });
        return;
      } catch (error) {
        // If user cancels or error occurs, fall back to LinkedIn URL
        console.log('Web Share API cancelled or failed, opening LinkedIn...');
      }
    }
    
    // Primary approach: Copy to clipboard AND open LinkedIn simultaneously
    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(fullShareText);
      
      // Open LinkedIn with pre-filled text
      const linkedInWindow = window.open(linkedInShareUrl, '_blank');
      
      // Show success message with additional instructions
      if (linkedInWindow) {
        // Give a moment for the window to open, then show the message
        setTimeout(() => {
          if (isPerfectScore()) {
            alert(`${uiTexts.results.sharing.perfectScore.title}\n\n${uiTexts.results.sharing.perfectScore.hiddenModeUnlocked}\n\n✅ LinkedIn opened with your CHAMPION result\n✅ Your perfect score text is copied to clipboard\n✅ Secret hashtag ${uiTexts.results.sharing.perfectScore.hashtag} included\n\n🎁 ${uiTexts.results.sharing.perfectScore.specialOffer}\n\n🚀 ${uiTexts.results.sharing.perfectScore.championText}`);
          } else {
            alert('🎉 Great! We\'ve opened LinkedIn for you and copied your result to clipboard!\n\n✅ LinkedIn should open with your text pre-filled\n✅ If the text isn\'t there, just paste it (Ctrl+V)\n✅ The demo link is included for others to try!\n\nShare your AI testing knowledge! 🚀');
          }
        }, 500);
      } else {
        // Pop-up blocked or failed to open
        if (isPerfectScore()) {
          alert(`${uiTexts.results.sharing.perfectScore.title}\n\n${uiTexts.results.sharing.perfectScore.championText}\n\n⚠️ Please allow pop-ups to auto-open LinkedIn\n\nManual steps:\n1. Visit: linkedin.com/feed\n2. Click "Start a post"\n3. Paste your PERFECT result (Ctrl+V)\n\n🎁 ${uiTexts.results.sharing.perfectScore.discountOffer}\n\n🚀 ${uiTexts.results.sharing.perfectScore.championText}`);
        } else {
          alert('🎉 Result copied to clipboard!\n\n⚠️ Please allow pop-ups to auto-open LinkedIn\n\nManual steps:\n1. Visit: linkedin.com/feed\n2. Click "Start a post"\n3. Paste your result (Ctrl+V)\n\nThe demo link is included! 🚀');
        }
      }
    } catch (clipboardError) {
      // Clipboard failed, but still try to open LinkedIn
      const linkedInWindow = window.open(linkedInShareUrl, '_blank');
      
      if (linkedInWindow) {
        // LinkedIn opened successfully
        setTimeout(() => {
          if (isPerfectScore()) {
            alert(`${uiTexts.results.sharing.perfectScore.title}\n\nLinkedIn opened with your CHAMPION result!\n\n✅ Your perfect score text should be pre-filled\n✅ If not, here's your PERFECT result to copy:\n\n${fullShareText}\n\n🎁 ${uiTexts.results.sharing.perfectScore.specialOffer}\n\n🚀 ${uiTexts.results.sharing.perfectScore.championText}`);
          } else {
            alert('🎉 LinkedIn opened with your result!\n\n✅ Your text should be pre-filled\n✅ If not, here\'s your result to copy:\n\n' + fullShareText + '\n\nShare your AI testing achievement! 🚀');
          }
        }, 500);
      } else {
        // Both clipboard and LinkedIn opening failed
        const manualUrl = 'https://www.linkedin.com/feed/';
        window.open(manualUrl, '_blank');
        
        // Show the text in a prompt so user can copy it manually
        if (isPerfectScore()) {
          prompt(`${uiTexts.results.sharing.perfectScore.title} Copy this CHAMPION text to share on LinkedIn:\n\n🎁 Include ${uiTexts.results.sharing.perfectScore.hashtag} for ${uiTexts.results.sharing.perfectScore.discountOffer}\n\n(LinkedIn should also open in a new tab)`, fullShareText);
        } else {
          prompt('Copy this text to share on LinkedIn:\n\n(LinkedIn should also open in a new tab)', fullShareText);
        }
      }
    }
  };

  if (currentView === 'welcome') {
    return (
      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* Dynamic background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 opacity-30">
          {[...Array(window.innerWidth < 768 ? 15 : 25)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-area-inset">
          <div className="max-w-6xl mx-auto text-center">
            
            {/* Language Selector - Fixed position - Always visible on welcome/intro screen */}
            <div className="fixed top-4 right-4 z-50">
              <LanguageSelector 
                currentLocale={currentLocale}
                onLocaleChange={changeLocale}
              />
            </div>

            {/* Debug Panel - DISABLED */}
            {false && (
              <div className="fixed top-4 left-4 z-50 bg-black/90 border-2 border-yellow-400 rounded-lg p-4 max-w-xs">
                <div className="text-yellow-400 font-bold text-sm mb-3">🔧 DEBUG MODE</div>
                <div className="space-y-2">
                  <button
                    onClick={() => setTestPerfectScore(!testPerfectScore)}
                    className={`w-full text-xs px-3 py-2 rounded ${testPerfectScore ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'} hover:bg-green-500 transition-colors`}
                  >
                    {testPerfectScore ? '🎯 Perfect Score: ON' : '🎯 Perfect Score: OFF'}
                  </button>
                  <button
                    onClick={() => {
                      // Quick test: Start quiz and immediately go to results
                      const questions = selectRandomQuestions();
                      setSelectedQuestions(questions);
                      if (testPerfectScore) {
                        setScore(questions.length); // Perfect score
                        setMaxStreak(questions.length);
                      } else {
                        setScore(Math.floor(questions.length * 0.8)); // 80% score
                        setMaxStreak(Math.floor(questions.length * 0.6));
                      }
                      setShowResults(true);
                      setCurrentView('quiz');
                    }}
                    className="w-full text-xs px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 transition-colors"
                  >
                    🚀 Quick Test Results
                  </button>
                  <div className="text-xs text-gray-400">
                    Shortcuts:<br/>
                    Ctrl+Shift+D: Toggle Debug<br/>
                    Ctrl+Shift+P: Toggle Perfect
                  </div>
                  <div className="text-xs text-gray-400">
                    URL: ?debug=true&test=perfect
                  </div>
                </div>
              </div>
            )}
            
            {/* Certification Badge */}
            <div className="mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out]">
              <div className="inline-flex items-center bg-gradient-to-r from-yellow-400/20 to-orange-400/20 border-2 border-yellow-400/40 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6">
                <Award className="mr-2 text-yellow-400" size={20} />
                <span className="text-yellow-400 font-bold text-sm sm:text-lg">{uiTexts.full.title}</span>
                <Sparkles className="ml-2 text-yellow-400" size={16} />
              </div>
            </div>
            
            {/* Header */}
            <div className="mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out]">
              <div className="relative">
                <Zap className="absolute -top-4 sm:-top-8 -left-4 sm:-left-8 text-yellow-400 animate-bounce" size={24} />
                <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent leading-tight">
                  {uiTexts.full.subtitle}
                </h1>
                <Trophy className="absolute -top-3 sm:-top-6 -right-5 sm:-right-10 text-yellow-400 animate-pulse" size={20} />
              </div>
              
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                  {uiTexts.full.features[0]}
                </h2>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-light mb-6 sm:mb-8 px-4 sm:px-0">
                  {uiTexts.full.description}
                </p>
              </div>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <CheckSquare className="text-white" size={24} />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-green-400 mb-3 sm:mb-4">{uiTexts.full.welcome.keyFeatures.expertQuestions.title}</h3>
                <div className="space-y-3 sm:space-y-4 text-gray-100 text-base sm:text-lg">
                  {uiTexts.full.welcome.keyFeatures.expertQuestions.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="text-green-400 mr-3 sm:mr-4" size={20} />
                      <span className="font-semibold">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full flex items-center justify-center">
                    <Brain className="text-white" size={24} />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-blue-400 mb-3 sm:mb-4">{uiTexts.full.welcome.keyFeatures.adaptiveSystem.title}</h3>
                <div className="space-y-3 sm:space-y-4 text-gray-100 text-base sm:text-lg">
                  {uiTexts.full.welcome.keyFeatures.adaptiveSystem.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="text-blue-400 mr-3 sm:mr-4" size={20} />
                      <span className="font-semibold">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                    <TrendingUp className="text-white" size={24} />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-purple-400 mb-3 sm:mb-4">{uiTexts.full.welcome.keyFeatures.detailedAnalytics.title}</h3>
                <div className="space-y-3 sm:space-y-4 text-gray-100 text-base sm:text-lg">
                  {uiTexts.full.welcome.keyFeatures.detailedAnalytics.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="text-purple-400 mr-3 sm:mr-4" size={20} />
                      <span className="font-semibold">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* What Makes This Different */}
            <div className="bg-gray-900/50 border border-gray-700/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.4s_both]">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                🎯 {uiTexts.full.welcome.whatMakesDifferent.title}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Shield className="text-white" size={24} />
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-red-400 mb-2">{uiTexts.full.welcome.whatMakesDifferent.expertCreated.title}</h4>
                  <p className="text-gray-300 text-sm sm:text-base">{uiTexts.full.welcome.whatMakesDifferent.expertCreated.description}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Target className="text-white" size={24} />
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-green-400 mb-2">{uiTexts.full.welcome.whatMakesDifferent.completeCoverage.title}</h4>
                  <p className="text-gray-300 text-sm sm:text-base">{uiTexts.full.welcome.whatMakesDifferent.completeCoverage.description}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Lightbulb className="text-white" size={24} />
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-blue-400 mb-2">{uiTexts.full.welcome.whatMakesDifferent.realScenarios.title}</h4>
                  <p className="text-gray-300 text-sm sm:text-base">{uiTexts.full.welcome.whatMakesDifferent.realScenarios.description}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <TrendingUp className="text-white" size={24} />
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-purple-400 mb-2">{uiTexts.full.welcome.whatMakesDifferent.trackProgress.title}</h4>
                  <p className="text-gray-300 text-sm sm:text-base">{uiTexts.full.welcome.whatMakesDifferent.trackProgress.description}</p>
                </div>
              </div>
            </div>


            {/* Perfect For Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-2 border-blue-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.8s_both]">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent text-center">
                🎯 {uiTexts.full.welcome.perfectFor.title}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {uiTexts.full.welcome.perfectFor.items.map((item, index) => {
                  const icons = [Users, GraduationCap, Award, Briefcase, Rocket, Trophy];
                  const colors = ['blue', 'purple', 'green', 'orange', 'pink', 'yellow'];
                  const Icon = icons[index % icons.length];
                  const color = colors[index % colors.length];
                  
                  return (
                    <div key={index} className="flex items-center bg-black/30 rounded-xl p-4 sm:p-6">
                      <Icon className={`text-${color}-400 mr-3 sm:mr-4`} size={24} />
                      <span className="text-white text-sm sm:text-base">{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Section */}
            <div className="animate-[fadeInUp_0.8s_ease-out_1s_both] px-4 sm:px-0">
              <button
                onClick={startQuiz}
                className="group relative inline-flex items-center px-8 sm:px-16 py-4 sm:py-6 text-xl sm:text-2xl md:text-3xl font-bold text-white bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 rounded-2xl sm:rounded-3xl hover:from-yellow-300 hover:via-pink-400 hover:to-purple-400 transform hover:scale-105 transition-all duration-300 shadow-2xl shadow-pink-500/30 w-full sm:w-auto"
              >
                <Play className="mr-3 sm:mr-4 group-hover:animate-bounce" size={28} />
                {uiTexts.full.welcome.startButton}
                <Zap className="ml-3 sm:ml-4 group-hover:animate-pulse" size={28} />
                
                {/* Animated glow */}
                <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300 -z-10"></div>
              </button>
              
              <p className="text-gray-400 mt-4 sm:mt-6 text-base sm:text-xl font-light">
                🎯 <strong className="text-white">Join professionals mastering AI testing</strong>
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-12 opacity-70 animate-[fadeInUp_0.8s_ease-out_1.2s_both]">
              {uiTexts.full.welcome.trustIndicators.map((indicator, index) => (
                <div key={index} className="flex items-center">
                  {index === 0 ? (
                    <Award className="mr-2 sm:mr-3 text-purple-400" size={20} />
                  ) : (
                    <Users className="mr-2 sm:mr-3 text-blue-400" size={20} />
                  )}
                  <span className="text-base sm:text-lg">{indicator}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const percentage = getScorePercentage();
    const passed = isPassed();
    const performance = getPerformanceLevel();
    
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
        
        {/* Language Selector - Fixed position - Hidden during quiz and results */}
        {!quizStarted && !showResults && (
          <div className="fixed top-4 right-4 z-50">
            <LanguageSelector 
              currentLocale={currentLocale}
              onLocaleChange={changeLocale}
            />
          </div>
        )}

        {/* Debug Panel - DISABLED */}
        {false && (
          <div className="fixed top-4 left-4 z-50 bg-black/90 border-2 border-yellow-400 rounded-lg p-4 max-w-xs">
            <div className="text-yellow-400 font-bold text-sm mb-3">🔧 DEBUG MODE</div>
            <div className="space-y-2">
              <button
                onClick={() => setTestPerfectScore(!testPerfectScore)}
                className={`w-full text-xs px-3 py-2 rounded ${testPerfectScore ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'} hover:bg-green-500 transition-colors`}
              >
                {testPerfectScore ? '🎯 Perfect Score: ON' : '🎯 Perfect Score: OFF'}
              </button>
              <div className="text-xs text-gray-300">
                Current: {getCorrectAnswersCount()}/{selectedQuestions.length}<br/>
                Perfect: {isPerfectScore() ? 'YES' : 'NO'}
              </div>
            </div>
          </div>
        )}
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-5xl mx-auto">
            
            {/* Results Header */}
            <div className="text-center mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out]">
              <div className="inline-flex items-center bg-gradient-to-r from-yellow-400/20 to-orange-400/20 border-2 border-yellow-400/40 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6">
                <Award className="mr-2 text-yellow-400" size={20} />
                <span className="text-yellow-400 font-bold text-sm sm:text-lg">{uiTexts.results.complete}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                {performance.emoji}
              </h1>
              
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                {performance.level}
              </h2>
            </div>

            {/* Score Display */}
            <div className={`${isPerfectScore() ? 'bg-gradient-to-br from-green-400/30 to-emerald-400/30 border-4 border-green-400/60 animate-[glow_2s_ease-in-out_infinite_alternate]' : 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/40'} rounded-2xl sm:rounded-3xl p-6 sm:p-12 mb-6 sm:mb-8 text-center animate-[fadeInUp_0.8s_ease-out_0.2s_both] relative overflow-hidden`}>
              <div className={`text-6xl sm:text-7xl md:text-9xl font-bold mb-4 sm:mb-6 bg-gradient-to-r ${isPerfectScore() ? 'from-green-400 to-emerald-400' : 'from-yellow-400 to-orange-400'} bg-clip-text text-transparent`}>
                {percentage}%
              </div>
              
              <div className="text-lg sm:text-xl md:text-3xl font-bold text-white mb-4 sm:mb-6 px-2 sm:px-0">
                {performance.message}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 max-w-2xl mx-auto">
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-1 sm:mb-2">{getCorrectAnswersCount()}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.correct}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-1 sm:mb-2">{selectedQuestions.length}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.questions}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-pink-400 mb-1 sm:mb-2">{maxStreak}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.bestStreak}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className={`${getAdaptiveFontSize(passed ? uiTexts.results.status.pass : uiTexts.results.status.study)} font-bold text-green-400 mb-1 sm:mb-2 leading-tight whitespace-nowrap`}>
                    {passed ? uiTexts.results.status.pass : uiTexts.results.status.study}
                  </div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.status}</div>
                </div>
              </div>
              
              {/* Perfect Score Easter Egg - Green gradient */}
              {isPerfectScore() && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Background gradient with gentle floating animation */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-emerald-400/20 to-teal-400/20 blur-3xl animate-[float_3s_ease-in-out_infinite]"></div>
                </div>
              )}
            </div>

            {/* Hidden Mode Achievement Block */}
            {isPerfectScore() && (
              <div className="bg-gradient-to-br from-green-400/30 via-emerald-400/30 to-teal-400/30 border-4 border-green-400/70 rounded-3xl p-8 sm:p-12 mb-6 sm:mb-8 text-center animate-[slideInScale_1s_ease-out_0.3s_both] relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400/15 via-emerald-400/15 to-teal-400/15 blur-2xl animate-[float_4s_ease-in-out_infinite_reverse]"></div>
                
                <div className="relative z-10">
                  <div className="text-6xl sm:text-8xl mb-6 animate-[wiggle_1s_ease-in-out_infinite]">
                    🎊💚🏆💚🎊
                  </div>
                  
                  <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent mb-4">
                    {uiTexts.results.sharing.perfectScore.hiddenModeTitle}
                  </h3>
                  
                  <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-6 border-2 border-green-400/50 mb-6">
                    <p className="text-xl sm:text-2xl text-white mb-4">
                      {uiTexts.results.sharing.perfectScore.russianCongratulations}
                    </p>
                    <p className="text-lg text-gray-200 mb-4">
                      {uiTexts.results.sharing.perfectScore.perfectScoreMessage}
                    </p>
                    <div className="bg-gradient-to-r from-amber-500/25 to-yellow-500/25 border-2 border-amber-400/60 rounded-xl p-4 mb-4 animate-[glowAmber_3s_ease-in-out_infinite_alternate] shadow-lg shadow-amber-500/20">
                      <p className="text-lg font-bold text-amber-300 mb-2">
                        {uiTexts.results.sharing.perfectScore.championOfferTitle}
                      </p>
                      <p className="text-base text-white">
                        {uiTexts.results.sharing.perfectScore.contactInstructions}
                      </p>
                    </div>
                    <p className="text-sm text-gray-300">
                      {uiTexts.results.sharing.perfectScore.linkedinButtonHint}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Share Section */}
            <div className={`${isPerfectScore() ? 'bg-gradient-to-r from-green-400/25 to-emerald-400/25 border-4 border-green-400/60' : 'bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-2 border-pink-500/40'} rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.6s_both]`}>
              <h3 className={`text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 ${isPerfectScore() ? 'bg-gradient-to-r from-green-400 to-emerald-400' : 'bg-gradient-to-r from-pink-400 to-purple-400'} bg-clip-text text-transparent`}>
                {isPerfectScore() ? uiTexts.results.sharing.perfectScore.title : uiTexts.results.sharing.title}
              </h3>
              <div className="flex justify-center">
                <button 
                  onClick={shareToLinkedIn}
                  className={`${isPerfectScore() ? 'bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400 text-white shadow-lg shadow-green-500/25' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25'} font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto`}
                >
                  <div className="flex items-center justify-center">
                    <Share2 className="mr-3" size={24} />
                    {isPerfectScore() ? `🏆 ${uiTexts.results.actions.shareLinkedIn} 🏆` : uiTexts.results.actions.shareLinkedIn}
                  </div>
                </button>
              </div>
              <p className="text-center text-gray-400 text-sm mt-3">
                {isPerfectScore() ? uiTexts.results.sharing.perfectScore.description : uiTexts.results.sharing.description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.8s_both]">
              <button
                onClick={() => setShowDetailedResults(!showDetailedResults)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                {showDetailedResults ? uiTexts.results.actions.hideReview : uiTexts.results.actions.showReview}
              </button>
              <button
                onClick={goToStart}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                <div className="flex items-center justify-center">
                  <Shuffle className="mr-2" size={20} />
                  {uiTexts.results.actions.tryAgainNew}
                </div>
              </button>
            </div>

            {/* 🎛️ Advanced Report Feature - Only show if enabled */}
            {isFeatureEnabled('ADVANCED_REPORT_ENABLED') && showDetailedResults && (
              <div className="mb-8 animate-[fadeInUp_0.8s_ease-out_0.5s_both]">
                <div className="relative group">
                  {/* Pulsing glow effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                  
                  <button
                    onClick={generateFullReport}
                    disabled={isGeneratingReport}
                    className="relative w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-300 hover:via-pink-400 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-center">
                      {isGeneratingReport ? (
                        <div className="w-full">
                          <div className="flex items-center justify-center mb-2">
                            <div className="relative">
                              <div className="animate-spin rounded-full h-8 w-8 border-4 border-white/30 border-t-white mr-3"></div>
                              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-300 animate-pulse mr-3"></div>
                            </div>
                            <span className="text-lg font-extrabold">🧠 Анализируем ваши ответы...</span>
                          </div>
                          <div className="w-full bg-white/20 rounded-full h-2 mb-2 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-full animate-pulse w-3/4"></div>
                          </div>
                          <div className="flex justify-center space-x-4 text-xs opacity-80">
                            <span className="animate-bounce">🔍 Анализ</span>
                            <span className="animate-bounce" style={{animationDelay: '0.2s'}}>🎭 Архетип</span>
                            <span className="animate-bounce" style={{animationDelay: '0.4s'}}>📊 Инсайты</span>
                            <span className="animate-bounce" style={{animationDelay: '0.6s'}}>🌟 Отчет</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Brain className="mr-3 animate-pulse" size={24} />
                          <span className="text-lg font-extrabold">🚀 GET FULL REVIEW</span>
                          <Sparkles className="ml-3 animate-bounce" size={24} />
                        </>
                      )}
                    </div>
                    
                    <div className="text-sm mt-1 opacity-90">
                      Мега-интерактивный анализ с 3D архетипом и космическими инсайтами! 
                    </div>
                  </button>
                </div>
                
                {/* Benefits list */}
                <div className="mt-4 text-center">
                  <div className="inline-flex flex-wrap gap-2 justify-center">
                    <span className="bg-yellow-400/20 text-yellow-300 px-3 py-1 rounded-full text-xs font-medium">🎭 3D Архетип</span>
                    <span className="bg-pink-400/20 text-pink-300 px-3 py-1 rounded-full text-xs font-medium">🌌 Космическая карта</span>
                    <span className="bg-purple-400/20 text-purple-300 px-3 py-1 rounded-full text-xs font-medium">💡 Персональные советы</span>
                    <span className="bg-blue-400/20 text-blue-300 px-3 py-1 rounded-full text-xs font-medium">📊 Интерактивные графики</span>
                  </div>
                </div>
                
                {/* Report Error Display */}
                {reportError && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                    <div className="text-red-400 text-sm">
                      <div className="flex items-center mb-2">
                        <span className="text-red-500 mr-2">⚠️</span>
                        <span className="font-semibold">Ошибка открытия отчета</span>
                      </div>
                      <p>{reportError}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Detailed Results */}
            {showDetailedResults && (
              <div className="bg-gray-900/50 border border-gray-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out]">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                  {uiTexts.review.title}
                </h3>
                <div className="space-y-4 sm:space-y-6 max-h-96 overflow-y-auto">
                  {selectedQuestions.map((question, index) => {
                    const userAnswer = selectedAnswers[question.id];
                    const isCorrect = userAnswer === question.correct;
                    
                    return (
                      <div key={question.id} className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 ${isCorrect ? 'border-green-500/40 bg-green-900/10' : 'border-red-500/40 bg-red-900/10'}`}>
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                              {index + 1}
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-400">
                              {question.chapter} • {question.difficulty}
                            </span>
                          </div>
                          {isCorrect ? (
                            <CheckCircle className="text-green-400" size={20} />
                          ) : (
                            <XCircle className="text-red-400" size={20} />
                          )}
                        </div>
                        <p className="text-white mb-3 sm:mb-4 text-base sm:text-lg">{question.text}</p>
                        <div className="space-y-2">
                          <p className={`text-sm sm:text-base ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                            <span className="font-semibold">{uiTexts.review.yourAnswer}</span> {userAnswer !== undefined ? question.options[userAnswer] : uiTexts.review.notAnswered}
                          </p>
                          {!isCorrect && (
                            <p className="text-green-400 text-sm sm:text-base">
                              <span className="font-semibold">{uiTexts.review.correctAnswer}</span> {question.options[question.correct]}
                            </p>
                          )}
                          <div className="bg-blue-900/20 border border-blue-500/30 p-3 sm:p-4 rounded-xl mt-3 sm:mt-4">
                            <p className="text-blue-300 text-sm sm:text-base">
                              <span className="font-semibold">💡 {uiTexts.review.explanation}</span> {userAnswer !== undefined ? getExplanationForAnswer(question, userAnswer) : question.expA}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ENHANCED COURSE PROMOTION IN RESULTS - Moved before Social Media */}
            <div className="relative bg-gradient-to-br from-yellow-400/20 via-orange-400/20 to-red-400/20 border-4 border-yellow-400/60 rounded-3xl sm:rounded-[2rem] p-8 sm:p-12 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.4s_both] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 opacity-10 blur-2xl"></div>
              
              <div className="relative z-10 text-center">
                <div className="inline-flex items-center bg-yellow-400/30 border-2 border-yellow-400/60 rounded-full px-6 sm:px-8 py-3 sm:py-4 mb-6 sm:mb-8">
                  <Crown className="mr-3 text-yellow-400" size={24} />
                  <span className="text-yellow-400 font-bold text-lg sm:text-xl">{uiTexts.results.course.fullCourse.nextLevel}</span>
                  <Sparkles className="ml-3 text-yellow-400" size={24} />
                </div>
                
                <h3 className="heading-font text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent mb-4 sm:mb-6">
                  {uiTexts.results.course.fullCourse.masterTitle}
                </h3>
                
                <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border-2 border-yellow-400/40 mb-8">
                  <div className="text-lg sm:text-xl text-gray-200 mb-6 leading-relaxed">
                    {uiTexts.results.course.fullCourse.masterDescription}
                  </div>
                  
                                      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/40 rounded-xl p-4 sm:p-6 mb-6">
                      <div className="text-center">
                        <div className="text-3xl sm:text-4xl font-bold text-green-400 mb-2">{uiTexts.results.course.fullCourse.bonusTitle}</div>
                        <div className="text-xl sm:text-2xl text-white">{uiTexts.results.course.fullCourse.bonusDescription}</div>
                      </div>
                    </div>
                </div>

                <a 
                  href="https://ainastasia.mykajabi.com/pl/2148634864" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 sm:px-12 py-6 sm:py-8 text-xl sm:text-2xl font-bold text-black bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl sm:rounded-3xl hover:from-yellow-300 hover:via-orange-300 hover:to-red-300 transform hover:scale-110 transition-all duration-300 shadow-2xl shadow-orange-500/50 w-full sm:w-auto"
                >
                  <Rocket className="mr-3 sm:mr-4" size={28} />
                  {uiTexts.results.course.fullCourse.claimButton}
                  <Star className="ml-3 sm:ml-4" size={28} />
                </a>
              </div>
            </div>

            {/* Feedback Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.9s_both]">
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                  {uiTexts.results.feedback.title}
                </h3>
                
                <a 
                  href="https://form.typeform.com/to/Qyxa1kQu" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                >
                  <Brain className="mr-2" size={20} />
                  {uiTexts.results.feedback.button}
                </a>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-2 border-purple-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 animate-[fadeInUp_0.8s_ease-out_1s_both]">
              <div className="text-center mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-3">
                  {uiTexts.results.social.title}
                </h3>
                <p className="text-gray-300 text-sm sm:text-base">
                  {uiTexts.results.social.description}
                </p>
              </div>
              
              <div className="flex justify-center gap-4 sm:gap-6">
                <a 
                  href="https://instagram.com/ai.nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-pink-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://tiktok.com/@ai.nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-pink-500 to-red-500 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-red-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.linkedin.com/in/ainastasia/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://t.me/ai_nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.youtube.com/@ainastasiia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-red-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a2.93 2.93 0 0 0-2.063-2.063C19.611 3.55 12 3.55 12 3.55s-7.611 0-9.435.573A2.93 2.93 0 0 0 .502 6.186C0 8.01 0 12 0 12s0 3.99.502 5.814a2.93 2.93 0 0 0 2.063 2.063C4.389 20.45 12 20.45 12 20.45s7.611 0 9.435-.573a2.93 2.93 0 0 0 2.063-2.063C24 15.99 24 12 24 12s0-3.99-.502-5.814zM9.546 15.569V8.431L15.818 12l-6.272 3.569z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Report Modal - добавляем в блок результатов */}
        {(() => {
          console.log('🎨 Results Modal Render check:', { showReportModal, reportUrl, showResults, isGeneratingReport });
          return null;
        })()}
        {showReportModal && reportUrl && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                console.log('🔒 Closing report modal by background click...');
                setShowReportModal(false);
                setReportUrl(null);
                setReportError(null);
                setIsGeneratingReport(false);
              }
            }}
          >
            <div className="bg-gray-900 rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl border border-gray-700/50">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Trophy className="text-yellow-400" size={24} />
                  Ваш персональный отчет
                </h2>
                <div className="flex items-center gap-3">
                  {/* Open in new tab button */}
                  <button
                    onClick={() => {
                      window.open(reportUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    title="Открыть в новой вкладке"
                  >
                    <Share2 size={16} />
                    Новая вкладка
                  </button>
                  
                  {/* Close button */}
                  <button
                    onClick={() => {
                      console.log('🔒 Closing report modal...');
                      setShowReportModal(false);
                      setReportUrl(null);
                      setReportError(null);
                      setIsGeneratingReport(false);
                      console.log('✅ Report modal closed and state cleared');
                    }}
                    className="flex items-center justify-center w-10 h-10 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="Закрыть"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {/* Modal Content - iframe с отчетом */}
              <div className="flex-1 p-4">
                <iframe
                  src={reportUrl}
                  className="w-full h-full border-0 rounded-lg bg-white"
                  title="Quiz Report"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        )}
        
      </div>
    );
  }

  // Function to get adaptive font size based on text length
  function getAdaptiveFontSize(text: string) {
    if (text.length <= 6) return "text-lg sm:text-xl lg:text-2xl xl:text-3xl";
    if (text.length <= 10) return "text-base sm:text-lg lg:text-xl xl:text-2xl";
    if (text.length <= 15) return "text-sm sm:text-base lg:text-lg xl:text-xl";
    return "text-xs sm:text-sm lg:text-base xl:text-lg";
  }

  const currentQ = selectedQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / selectedQuestions.length) * 100;

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
      
      {/* Mobile Language Selector - Top right - Hidden during quiz and results */}
      {!quizStarted && !showResults && (
        <div className="block sm:hidden fixed top-2 right-2 z-50">
          <LanguageSelector 
            currentLocale={currentLocale}
            onLocaleChange={changeLocale}
            isMobile={true}
          />
        </div>
      )}
      
      <div className="relative z-10 container mx-auto px-3 sm:px-6 pt-4 pb-2 sm:py-6 safe-area-inset">
        
        {/* Mobile status bar spacing */}
        <div className="h-safe-top sm:h-0"></div>
        
        {/* Force Finish button removed per user request */}
        
        {/* Ultra Compact Header for Mobile */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-500/40 rounded-lg sm:rounded-xl p-2 sm:p-4 mb-2 sm:mb-6">
          {/* Mobile Layout */}
          <div className="block sm:hidden">
            {/* Mobile Header Content */}
            <div className="flex items-center justify-between mb-3 pr-12">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  {uiTexts.full.title}
                </h1>
                <span className="text-gray-400 text-sm">
                  {currentQuestion + 1}/{selectedQuestions.length}
                </span>
              </div>
              <div className="bg-black/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-yellow-400/40">
                <div className="flex items-center text-white">
                  <Clock className="mr-2 text-yellow-400" size={16} />
                  <span className="text-sm font-mono text-yellow-400">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Ultra Compact Progress Bar for Mobile */}
            <div className="relative">
              <div className="bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-start text-xs text-gray-400 mt-1">
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden sm:block">
            {/* Desktop Header and Timer - Same level */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  {uiTexts.quiz.header.title}
                </h1>
                <span className="text-gray-400 text-sm">
                  {currentQuestion + 1}/{selectedQuestions.length}
                </span>
              </div>
              <div className="flex items-center text-white bg-yellow-400/20 px-3 py-1.5 rounded-lg border border-yellow-400/40">
                <Clock className="mr-2 text-yellow-400" size={16} />
                <span className="text-base font-mono text-yellow-400">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
            
            {/* Desktop Progress Bar */}
            <div className="relative mt-2">
              <div className="bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-start text-xs text-gray-400 mt-1">
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Language Selector - Far outside block - Hidden during quiz and results */}
        {!quizStarted && !showResults && (
          <div className="hidden sm:block fixed top-4 right-4 z-50">
            <LanguageSelector 
              currentLocale={currentLocale}
              onLocaleChange={changeLocale}
            />
          </div>
        )}

        {/* Chapter/Topic - Center below progress bar */}
        {currentQ && (
          <div className="text-center mb-4">
            <div className="inline-block bg-gray-800/60 backdrop-blur-sm px-4 py-2 rounded-lg border border-gray-600/40">
              <div className="text-sm text-gray-300 font-medium">
                {currentQ.chapter}
              </div>
            </div>
          </div>
        )}

        {/* Question */}
        {currentQ && (
          <div 
            className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-2 border-gray-600/50 rounded-lg sm:rounded-xl p-2 sm:p-4 mb-2 sm:mb-6 animate-[fadeInUp_0.6s_ease-out] relative"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Swipe indicator for mobile */}
            <div className="block sm:hidden absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-600 rounded-full"></div>
            
            {/* Mobile Question Header */}
            <div className="block sm:hidden">
              <div className="flex justify-between items-center mb-2 pt-2">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${
                    currentQ.difficulty === 'K1' ? 'bg-green-500' : 
                    currentQ.difficulty === 'K2' ? 'bg-yellow-500' : 
                    currentQ.difficulty === 'K3' ? 'bg-orange-500' : 'bg-red-500'
                  }`}>
                    {currentQuestion + 1}
                  </span>
                  <h2 className="text-sm font-bold text-white">
                    {currentQ.difficulty === 'K1' ? `🟢 ${uiTexts.common.difficulty.k1}` : 
                     currentQ.difficulty === 'K2' ? `🟡 ${uiTexts.common.difficulty.k2}` : 
                     currentQ.difficulty === 'K3' ? `🟠 ${uiTexts.common.difficulty.k3}` : `🔴 ${uiTexts.common.difficulty.k4}`}
                  </h2>
                </div>
                <div className="text-lg">💭</div>
              </div>
              
              <p className="text-white text-base leading-snug mb-3">
                {currentQ.text}
              </p>

              {/* Mobile Options */}
              <div className="space-y-2">
                {currentQ.options.map((option: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQ.id, index)}
                    className={`w-full text-left p-2.5 rounded-lg border-2 transition-all duration-300 transform min-h-[44px] touch-manipulation active:scale-[0.98] ${
                      selectedAnswers[currentQ.id] === index
                        ? 'border-pink-500 bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white shadow-lg shadow-pink-500/25'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-pink-400/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className={`font-bold mr-2.5 text-sm min-w-[1.25rem] h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                        selectedAnswers[currentQ.id] === index 
                          ? 'bg-pink-500 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base leading-snug">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Swipe hint for mobile */}
              <div className="text-center text-xs text-gray-500 mt-2 mb-4">
                {uiTexts.quiz.navigation.swipeHint}
              </div>
              
              {/* Question Overview for Mobile */}
              <div className="bg-gray-900/30 border border-gray-700/30 rounded-lg p-2 mb-4">
                <h3 className="text-white font-bold mb-2 flex items-center text-xs">
                  <Menu className="mr-1 text-yellow-400" size={14} />
                  {uiTexts.common.questions}
                </h3>
                <div className="grid grid-cols-8 gap-1.5">
                  {selectedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestionWithFeedback(index)}
                      className={`aspect-square rounded-md text-xs font-bold transition-all duration-300 transform active:scale-95 min-h-[28px] ${
                        index === currentQuestion
                          ? 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 text-white shadow-lg'
                          : selectedAnswers[question.id] !== undefined
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 text-gray-300'
                      }`}
                      title={`${uiTexts.common.question} ${index + 1} - ${question.difficulty}${selectedAnswers[question.id] !== undefined ? ` (${uiTexts.quiz.status.answered})` : ` (${uiTexts.quiz.status.notAnswered})`}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Desktop Question Layout */}
            <div className="hidden sm:block">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-sm sm:text-base font-bold ${
                    currentQ.difficulty === 'K1' ? 'bg-green-500' : 
                    currentQ.difficulty === 'K2' ? 'bg-yellow-500' : 
                    currentQ.difficulty === 'K3' ? 'bg-orange-500' : 'bg-red-500'
                  }`}>
                    {currentQuestion + 1}
                  </span>
                  <h2 className="text-base sm:text-lg font-bold text-white">
                    {currentQ.difficulty === 'K1' ? `🟢 ${uiTexts.common.difficulty.k1}` : 
                     currentQ.difficulty === 'K2' ? `🟡 ${uiTexts.common.difficulty.k2}` : 
                     currentQ.difficulty === 'K3' ? `🟠 ${uiTexts.common.difficulty.k3}` : `🔴 ${uiTexts.common.difficulty.k4}`}
                  </h2>
                </div>
                <div className="text-xl sm:text-2xl">💭</div>
              </div>
              
              <p className="text-white text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                {currentQ.text}
              </p>

              {/* Desktop Options */}
              <div className="space-y-2 sm:space-y-3">
                {currentQ.options.map((option: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQ.id, index)}
                    className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-all duration-300 transform min-h-[50px] sm:min-h-[60px] touch-manipulation hover:scale-[1.01] active:scale-[0.99] ${
                      selectedAnswers[currentQ.id] === index
                        ? 'border-pink-500 bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white shadow-lg shadow-pink-500/25'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-pink-400/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className={`font-bold mr-3 text-base sm:text-lg min-w-[1.5rem] sm:min-w-[2rem] h-6 w-6 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedAnswers[currentQ.id] === index 
                          ? 'bg-pink-500 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base sm:text-lg leading-relaxed">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Navigation - Fixed on mobile with safe area */}
        <div className="fixed sm:static bottom-0 left-0 right-0 bg-black/95 sm:bg-transparent backdrop-blur-sm sm:backdrop-blur-none border-t sm:border-t-0 border-gray-700/50 p-3 sm:p-0 sm:mb-4 z-50 pb-safe-bottom">
          <div className="flex justify-between items-center gap-3 max-w-lg sm:max-w-none mx-auto">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestion === 0}
              className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 text-white rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 min-w-[60px] sm:min-w-[80px] min-h-[40px] touch-manipulation"
            >
              <ArrowLeft className="mr-1 sm:mr-2" size={16} />
              <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.previous}</span>
            </button>

            <div className="text-center flex-1 max-w-[100px] sm:max-w-none">
              <div className="text-white text-sm sm:text-base font-medium">
                {Object.keys(selectedAnswers).length}/{selectedQuestions.length}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">
                {uiTexts.quiz.status.current}
              </div>
            </div>

            {currentQuestion === selectedQuestions.length - 1 ? (
              <button
                onClick={() => {
                  console.log('🏁 Quiz finish button clicked!');
                  handleFinishQuiz(); // Только показать результаты
                }}
                className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 hover:from-yellow-300 hover:via-pink-400 hover:to-purple-400 text-white rounded-lg transition-all duration-300 font-bold transform hover:scale-105 active:scale-95 shadow-lg shadow-pink-500/25 min-w-[70px] sm:min-w-[100px] min-h-[40px] touch-manipulation"
              >
                <Trophy className="mr-1 sm:mr-2" size={16} />
                <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.finish}</span>
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 min-w-[60px] sm:min-w-[80px] min-h-[40px] touch-manipulation"
              >
                <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.next}</span>
                <ArrowRight className="ml-1 sm:ml-2" size={16} />
              </button>
            )}
          </div>
        </div>



        {/* Mobile spacing for fixed navigation with safe area */}
        <div className="h-20 sm:h-0"></div>

        {/* Question Overview for Desktop only */}
        <div className="hidden sm:block bg-gray-900/30 border border-gray-700/30 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3 flex items-center text-base">
            <Menu className="mr-2 text-yellow-400" size={14} />
            {uiTexts.quiz.navigation.questionOverview}
          </h3>
          <div className="grid grid-cols-10 md:grid-cols-12 gap-2">
            {selectedQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionWithFeedback(index)}
                className={`aspect-square rounded-lg text-xs font-bold transition-all duration-300 transform hover:scale-110 min-h-[36px] ${
                  index === currentQuestion
                    ? 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 text-white shadow-lg'
                    : selectedAnswers[question.id] !== undefined
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                title={`${uiTexts.common.question} ${index + 1} - ${question.difficulty}${selectedAnswers[question.id] !== undefined ? ` (${uiTexts.quiz.status.answered})` : ` (${uiTexts.quiz.status.notAnswered})`}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};



export default ISTQBAIFullQuiz;